import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const EXPERIENCE = [
  {
    date: 'APR 2025 — PRESENT',
    role: 'Full Stack Developer',
    company: 'Axion Solutions',
    companyUrl: 'https://axionsolutionsltd.com/',
    summary:
      'Spearheading the end-to-end development of WinaContract, a platform centralising UK public procurement data. Built robust backend systems with Python, Django, and PostgreSQL, including automated data ingestion pipelines from OCDS APIs. Developed a responsive React and TypeScript frontend with dynamic filtering and CPV hierarchy navigation. Configured CI/CD pipelines and managed cloud deployment.',
    tags: ['Python', 'Django', 'PostgreSQL', 'React', 'TypeScript', 'Vite', 'Docker', 'CI/CD'],
  },
  {
    date: 'NOV 2024 — APR 2025',
    role: 'Software Engineer Intern',
    company: 'Arup',
    summary:
      'Developed and maintained full-stack web applications using React and TypeScript, improving team workflow efficiency by 25%. Built responsive UIs with micro-frontend architecture, maintained CI/CD pipelines using GitHub Actions reducing deployment time by 35%, and designed GraphQL and RESTful APIs integrating with SQL databases.',
    tags: ['React', 'TypeScript', 'GraphQL', 'Express.js', 'GitHub Actions', 'CI/CD'],
  },
  {
    date: 'JULY 2024 — OCT 2024',
    role: 'Software Engineering Student',
    company: 'General Assembly',
    summary:
      'Completed a 12-week, 450-hour immersive full-stack software engineering program mastering object-oriented programming and MVC frameworks. Built full-stack applications with React.js and Express.js, and developed RESTful APIs using Node.js and TypeScript, achieving 95% test coverage and reducing bugs by 30%.',
    tags: ['React', 'Node.js', 'Express.js', 'TypeScript', 'REST APIs'],
  },
  {
    date: 'JULY 2023',
    role: 'Structural Engineer Intern',
    company: 'Arup',
    summary:
      'Analysed and optimised 15+ complex structural models, reducing simulation processing time by 30%. Collaborated with senior engineers to deliver major structural analysis projects on schedule, and developed Python scripts to automate repetitive analysis tasks, reducing manual processing time by 40%.',
    tags: ['Python', 'Structural Analysis'],
  },
];

function ExperienceCard({ item, index, hoveredIndex, onHover, onLeave }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const isHovered = hoveredIndex === index;
  const isDimmed = hoveredIndex !== null && !isHovered;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={onLeave}
      style={{
        display: 'flex',
        gap: '24px',
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: isHovered ? 'rgba(30, 41, 59, 0.5)' : 'transparent',
        border: `1px solid ${isHovered ? 'rgba(96, 165, 250, 0.08)' : 'transparent'}`,
        opacity: isDimmed ? 0.5 : 1,
        transition: 'background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
        cursor: 'default',
      }}
    >
      {/* Date */}
      <div
        style={{
          fontSize: '12px',
          fontFamily: 'monospace',
          color: '#64748b',
          marginTop: '3px',
          width: '160px',
          flexShrink: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {item.date}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 500,
            color: isHovered ? '#60a5fa' : '#e2e8f0',
            marginBottom: '12px',
            transition: 'color 0.2s ease',
            lineHeight: 1.4,
          }}
        >
          {item.role} ·{' '}
          {item.companyUrl ? (
            <a href={item.companyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = '#60a5fa'} onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}>{item.company}</a>
          ) : (
            <span style={{ color: '#94a3b8' }}>{item.company}</span>
          )}
          <span
            style={{
              display: 'inline-block',
              transform: isHovered ? 'translate(2px, -2px)' : 'translate(0, 0)',
              transition: 'transform 0.2s ease, opacity 0.2s ease',
              fontSize: '14px',
              color: '#60a5fa',
              opacity: isHovered ? 1 : 0,
              marginLeft: '6px',
            }}
          >
            ↗
          </span>
        </h3>

        <p style={{ fontSize: '14px', lineHeight: 1.7, color: '#94a3b8', marginBottom: '16px' }}>
          {item.summary}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {item.tags.map((tag) => (
            <span
              key={tag}
              style={{
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(96, 165, 250, 0.1)',
                color: '#60a5fa',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Experience() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="experience" style={{ marginBottom: '128px' }}>
      <h2
        style={{
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#60a5fa',
          marginBottom: '40px',
        }}
      >
        Experience
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {EXPERIENCE.map((item, i) => (
          <ExperienceCard
            key={i}
            item={item}
            index={i}
            hoveredIndex={hoveredIndex}
            onHover={setHoveredIndex}
            onLeave={() => setHoveredIndex(null)}
          />
        ))}
      </div>
    </section>
  );
}
